import React, {useMemo} from 'react';

import {IApplicationJourneyStepDefinition} from './journeyConfig';
import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import ApplicationJourneySubStepProgress from './ApplicationJourneySubStepProgress';

interface ApplicationJourneyPlaceholderStepProps {
  step: IApplicationJourneyStepDefinition;
  activeSubStepIndex: number;
  applicationId?: string;
  farmName: string;
  companyName: string;
  onPrevious?: () => void;
  onNext?: () => void;
  onSubStepClick?: (index: number) => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

const ApplicationJourneyPlaceholderStep: React.FC<
  ApplicationJourneyPlaceholderStepProps
> = ({
  step,
  activeSubStepIndex,
  applicationId,
  farmName,
  companyName,
  onPrevious,
  onNext,
  onSubStepClick,
  hasPrevious,
  hasNext,
}) => {
  const activeSubStep = useMemo(() => {
    if (!step.subSteps || step.subSteps.length === 0) return null;
    return step.subSteps[activeSubStepIndex] || step.subSteps[0];
  }, [activeSubStepIndex, step.subSteps]);

  const currentTitle = activeSubStep?.title || step.title;
  const currentDescription = activeSubStep?.description || step.description;
  const progressLabel = activeSubStep
    ? `${activeSubStepIndex + 1} of ${step.subSteps?.length || 0} sub-steps`
    : 'Shell ready';

  return (
    <>
      {step.subSteps && step.subSteps.length > 0 ? (
        <ApplicationJourneySubStepProgress
          steps={step.subSteps.map(({key, title}) => ({key, title}))}
          activeIndex={activeSubStepIndex}
          ariaLabel={`${step.title} sub-steps`}
          onStepClick={onSubStepClick}
        />
      ) : null}
      <ApplicationJourneyStepLayout
        title={currentTitle}
        description={currentDescription}
        secondaryAction={
          hasPrevious && onPrevious
            ? {
                label: 'Back',
                onClick: onPrevious,
              }
            : undefined
        }
        primaryAction={
          hasNext && onNext
            ? {
                label: 'Continue',
                onClick: onNext,
              }
            : undefined
        }
      >
        <div className="journey-placeholder">
          <section className="journey-placeholder__panel">
            <p className="journey-placeholder__eyebrow">Current section</p>
            <h3>{currentTitle}</h3>
            <p className="journey-placeholder__copy">
              The step 1 journey shell is in place. The full section content for
              this area will be migrated in the next prompts while keeping this
              layout stable.
            </p>
          </section>

          <section className="journey-placeholder__meta-grid">
            <article className="journey-placeholder__meta-card">
              <span className="journey-placeholder__meta-label">
                Application
              </span>
              <strong>{applicationId || 'Pending'}</strong>
            </article>
            <article className="journey-placeholder__meta-card">
              <span className="journey-placeholder__meta-label">Farm</span>
              <strong>{farmName}</strong>
            </article>
            <article className="journey-placeholder__meta-card">
              <span className="journey-placeholder__meta-label">Company</span>
              <strong>{companyName}</strong>
            </article>
            <article className="journey-placeholder__meta-card">
              <span className="journey-placeholder__meta-label">Status</span>
              <strong>{progressLabel}</strong>
            </article>
          </section>

          {step.subSteps && step.subSteps.length > 0 ? (
            <section className="journey-placeholder__panel">
              <p className="journey-placeholder__eyebrow">Sub-step flow</p>
              <h3>{activeSubStep?.title}</h3>
              <p className="journey-placeholder__copy">
                This section already supports sub-step navigation in the shell.
                Later prompts will replace this placeholder with the fully
                ported UI from khula-inputs-web.
              </p>
            </section>
          ) : null}
        </div>
      </ApplicationJourneyStepLayout>
    </>
  );
};

export default ApplicationJourneyPlaceholderStep;
